/**
 * @fileoverview
 * Workflow Ticker section for the Aideco homepage.
 *
 * This is a client component so that useLingui can translate ticker items.
 *
 * Content (7 items):
 * - Photoreal Renders
 * - 3D Visualization
 * - Clay Models
 * - Sketch Style
 * - Exterior Views
 * - Interior Scenes
 * - Material Styling
 *
 * Design:
 * - CSS-based infinite scroll animation (performant, no JS)
 * - Warm cream background (#FAF8F5) matching aideco.us
 * - Pill-card items in rounded rectangle cards
 * - Duplicate items for seamless loop
 * - Responsive layout
 *
 * Animation:
 * - Uses homepage-ticker-scroll keyframe from homepage-tokens.css
 * - 25s duration for smooth scrolling
 * - translate3d for GPU acceleration
 */

"use client";

import { useLingui } from "@lingui/react/macro";

/**
 * WorkflowTicker renders an infinite horizontal scrolling marquee
 * displaying workflow capabilities as pill-card items.
 *
 * @returns JSX.Element
 */
export function WorkflowTicker() {
  const { t } = useLingui();

  // Workflow ticker items (translated)
  const workflowItems = [
    t`Photoreal Renders`,
    t`3D Visualization`,
    t`Clay Models`,
    t`Sketch Style`,
    t`Exterior Views`,
    t`Interior Scenes`,
    t`Material Styling`,
  ];

  return (
    <section
      aria-label={t`Workflow capabilities ticker`}
      className="relative w-full overflow-hidden bg-[#FAF8F5] py-10 sm:py-14"
    >
      {/* Ticker track with animation - continuous scrolling */}
      <div
        className="flex items-center gap-4 whitespace-nowrap"
        style={{
          animation: "homepage-ticker-scroll 25s linear infinite",
          willChange: "transform",
        }}
      >
        {/* Original items — accessible to screen readers */}
        {workflowItems.map((item, index) => (
          /* Pill-card item */
          <span
            key={`${item}-${index}`}
            className="inline-flex items-center rounded-2xl border border-stone-200 bg-white px-6 py-4 text-xl font-medium text-stone-700 shadow-sm sm:px-8 sm:py-5 sm:text-2xl"
          >
            {item}
          </span>
        ))}
        {/* Duplicate set for seamless loop — hidden from assistive technology */}
        <div aria-hidden="true" className="contents">
          {workflowItems.map((item, index) => (
            <span
              key={`dup-${item}-${index}`}
              className="inline-flex items-center rounded-2xl border border-stone-200 bg-white px-6 py-4 text-xl font-medium text-stone-700 shadow-sm sm:px-8 sm:py-5 sm:text-2xl"
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
